import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { Check, Download, Plus, Search, Trash2, X } from 'lucide-react';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { DateRangeFilter } from '../../components/admin/DateRangeFilter';
import { SortableHeaderCell } from '../../components/admin/SortableHeaderCell';
import { brandChipSx } from '../../components/admin/brandChip';
import { DateRangeValue, EMPTY_DATE_RANGE, dateRangeToIso } from '../../components/admin/dateRange';
import { nextSort } from '../../components/admin/nextSort';
import { useDebouncedValue } from '../../components/admin/useDebouncedValue';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { httpGET } from '../../services/http';
import { subscriptionReducer } from '../../store/reducers/subscription.reducer';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { userReducer } from '../../store/reducers/user.reducer';
import {
  getAllSubscriptions,
  getAssignSubscriptionSuccess,
  getCancelSubscriptionSuccess,
  getIsAssigningSubscription,
  getIsCancellingSubscription,
  getIsLoadingAllSubscriptions,
  getSubscriptionError,
} from '../../store/selectors/subscription.selector';
import { getAllUsers, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { greyP5 } from '../../theme.ts';
import {
  PageResponse,
  SortDirection,
  SubscriptionListItem,
  SubscriptionListQuery,
  SubscriptionListSort,
  SubscriptionTier,
  buildSubscriptionListUrl,
} from '../../types/subscription';
import { User } from '../../types/user';
import { downloadCsv } from '../../utils/csv';

const { loadAllActiveSubscriptions, assignSubscription, cancelSubscription, resetSubscriptionStatus } =
  subscriptionReducer.actions;
const { loadUsers } = userReducer.actions;
const { showToast } = toastReducer.actions;

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
/** Largest page the backend serves; the CSV export walks the pages at this size. */
const EXPORT_PAGE_SIZE = 200;
/** Text columns start A→Z, the tier lowest plan first, dates and credits newest / largest first. */
const DEFAULT_DIRECTION: Record<SubscriptionListSort, SortDirection> = {
  NAME: 'ASC',
  EMAIL: 'ASC',
  TIER: 'ASC',
  STARTED_AT: 'DESC',
  REGISTERED: 'DESC',
  CREDITS: 'DESC',
};

const tiers: SubscriptionTier[] = ['TRIAL', 'FREE', 'PROFESSIONAL', 'ULTIMATE'];
const tierLabel = (tier: SubscriptionTier) => tier.charAt(0) + tier.slice(1).toLowerCase();

const filterFieldSx = { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)' } };
const mutedSx = { color: 'rgba(255, 255, 255, 0.5)' };

export const SubscriptionsPage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const allSubscriptions = useAppSelector(getAllSubscriptions);
  const isLoading = useAppSelector(getIsLoadingAllSubscriptions);
  const isAssigning = useAppSelector(getIsAssigningSubscription);
  const isCancelling = useAppSelector(getIsCancellingSubscription);
  const error = useAppSelector(getSubscriptionError);
  const assignSuccess = useAppSelector(getAssignSubscriptionSuccess);
  const cancelSuccess = useAppSelector(getCancelSubscriptionSuccess);
  const allUsers = useAppSelector(getAllUsers);

  // Search, filter, sorting and paging; all applied on the server
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const [tierFilter, setTierFilter] = useState<SubscriptionTier | ''>('');
  const [registered, setRegistered] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const [sort, setSort] = useState<{ sort: SubscriptionListSort; direction: SortDirection }>({
    sort: 'STARTED_AT',
    direction: 'DESC',
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [isExporting, setIsExporting] = useState(false);

  // Modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>('FREE');
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<SubscriptionListItem | null>(null);

  const isAdmin = !!user && user.roles.includes('ADMIN');

  // Check if user is admin, redirect to forbidden if not
  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  // A new search, filter or page size starts again from the first page. Reset while rendering (not in an
  // effect) so the list is requested once, for page 0, instead of first for the old page.
  const filtersKey = [debouncedSearch, tierFilter, registered.from, registered.to, pageSize].join('|');
  const [pageFiltersKey, setPageFiltersKey] = useState(filtersKey);
  if (pageFiltersKey !== filtersKey) {
    setPageFiltersKey(filtersKey);
    setPage(0);
  }

  const query: SubscriptionListQuery = useMemo(() => {
    const registeredIso = dateRangeToIso(registered);
    return {
      tier: tierFilter || undefined,
      search: debouncedSearch || undefined,
      registeredFrom: registeredIso.from,
      registeredTo: registeredIso.to,
      sort: sort.sort,
      direction: sort.direction,
      page,
      pageSize,
    };
  }, [tierFilter, debouncedSearch, registered, sort, page, pageSize]);

  useEffect(() => {
    if (isAdmin) {
      dispatch(loadAllActiveSubscriptions(query));
    }
  }, [dispatch, isAdmin, query]);

  // Users are only needed for the assign autocomplete
  useEffect(() => {
    if (isAdmin) {
      dispatch(loadUsers());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (assignSuccess || cancelSuccess) {
      setAssignModalOpen(false);
      setCancelModalOpen(false);
      setSelectedUser(null);
      setSubscriptionToCancel(null);
      // Success is handled by saga reloading subscriptions, we just reset the status here
      const timeout = setTimeout(() => {
        dispatch(resetSubscriptionStatus());
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [assignSuccess, cancelSuccess, dispatch]);

  // Don't render anything if user is not admin (will redirect)
  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  const handleSort = (column: SubscriptionListSort) => {
    setSort((current) => nextSort(current, column, DEFAULT_DIRECTION[column]));
    setPage(0);
  };

  const handleOpenAssignModal = () => {
    setAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedUser(null);
    setSelectedTier('FREE');
    dispatch(resetSubscriptionStatus());
  };

  const handleOpenCancelModal = (sub: SubscriptionListItem) => {
    setSubscriptionToCancel(sub);
    setCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    setSubscriptionToCancel(null);
    dispatch(resetSubscriptionStatus());
  };

  const handleAssign = () => {
    if (selectedUser) {
      dispatch(assignSubscription({ userId: selectedUser.id, request: { tier: selectedTier } }));
    }
  };

  // Handle Esc key to close the modal
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && !isAssigning && !isCancelling) {
      event.stopPropagation();
      handleCloseAssignModal();
      handleCloseCancelModal();
    }
  };

  const handleCancel = () => {
    if (subscriptionToCancel) {
      dispatch(cancelSubscription(subscriptionToCancel.userId));
    }
  };

  /** Exports every subscription matching the current search, tier and sort, not just the visible page. */
  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const items: SubscriptionListItem[] = [];
      for (let exportPage = 0; ; exportPage++) {
        const { data }: { data: PageResponse<SubscriptionListItem> } = await httpGET(
          buildSubscriptionListUrl({ ...query, page: exportPage, pageSize: EXPORT_PAGE_SIZE }),
        );
        items.push(...data.content);
        if (!data.hasNext) break;
      }
      downloadCsv(
        `user-subscriptions-${new Date().toISOString().split('T')[0]}.csv`,
        [
          'User ID',
          'Full Name',
          'Email',
          'Registered At',
          'Tier',
          'Started At',
          'Credits Granted',
          'Last Credit Grant',
        ],
        items.map((sub) => [
          sub.userId,
          sub.userFullName ?? '',
          sub.userEmail,
          new Date(sub.userRegisteredAt).toISOString(),
          sub.tier,
          new Date(sub.startedAt).toISOString(),
          sub.creditsGranted,
          sub.lastCreditGrantAt ? new Date(sub.lastCreditGrantAt).toISOString() : '',
        ]),
      );
    } catch {
      dispatch(showToast({ message: 'Exporting subscriptions failed', type: 'error' }));
    } finally {
      setIsExporting(false);
    }
  };

  const rows = allSubscriptions?.content ?? [];
  const totalElements = allSubscriptions?.totalElements ?? 0;
  const hasFilters = !!debouncedSearch || !!tierFilter || !!registered.from || !!registered.to;
  const sortProps = { sort: sort.sort, direction: sort.direction, onSort: handleSort };

  return (
    <Box>
      <AdminPageHeader
        title="User Subscriptions"
        subtitle="The active plan of every user."
        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={isExporting ? <CircularProgress size={16} /> : <Download size={16} />}
              onClick={handleExportCsv}
              disabled={!totalElements || isExporting}
              sx={{
                borderRadius: '24px',
                px: 2,
                color: 'rgba(255, 255, 255, 0.85)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': { borderColor: '#B3EC49', color: '#B3EC49' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={handleOpenAssignModal}
              sx={{
                bgcolor: '#B3EC49',
                color: 'black',
                '&:hover': {
                  bgcolor: 'rgba(179, 236, 73, 0.8)',
                },
                borderRadius: '24px',
                px: 3,
              }}
            >
              Assign Subscription
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(resetSubscriptionStatus())}>
          {error}
        </Alert>
      )}

      {(assignSuccess || cancelSuccess) && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Operation completed successfully!
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          placeholder="Search name or email"
          inputProps={{ 'aria-label': 'Search name or email' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}>
                  <X size={14} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ minWidth: 280, flexGrow: { xs: 1, sm: 0 }, ...filterFieldSx }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="tier-filter-label">Tier</InputLabel>
          <Select
            labelId="tier-filter-label"
            value={tierFilter}
            label="Tier"
            onChange={(e) => setTierFilter(e.target.value as SubscriptionTier | '')}
            sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
          >
            <MenuItem value="">All tiers</MenuItem>
            {tiers.map((tier) => (
              <MenuItem key={tier} value={tier}>
                {tierLabel(tier)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DateRangeFilter label="Registered" value={registered} onChange={setRegistered} />
        <Typography variant="body2" sx={{ ...mutedSx, ml: { sm: 'auto' } }}>
          {totalElements} {totalElements === 1 ? 'subscription' : 'subscriptions'}
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Box sx={{ height: 2 }}>{isLoading && allSubscriptions && <LinearProgress sx={{ height: 2 }} />}</Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableHeaderCell column="NAME" {...sortProps}>
                  Name
                </SortableHeaderCell>
                <SortableHeaderCell column="EMAIL" {...sortProps}>
                  Email
                </SortableHeaderCell>
                <SortableHeaderCell column="REGISTERED" {...sortProps}>
                  Registered
                </SortableHeaderCell>
                <SortableHeaderCell column="TIER" {...sortProps}>
                  Tier
                </SortableHeaderCell>
                <SortableHeaderCell column="STARTED_AT" {...sortProps}>
                  Started
                </SortableHeaderCell>
                <SortableHeaderCell column="CREDITS" {...sortProps} align="right">
                  Credits granted
                </SortableHeaderCell>
                <TableCell>Last credit grant</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ opacity: isLoading && allSubscriptions ? 0.6 : 1, transition: 'opacity 150ms' }}>
              {!allSubscriptions && isLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {allSubscriptions && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasFilters ? 'No subscriptions match the search or filter.' : 'No active subscriptions found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((sub) => (
                <TableRow key={sub.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {sub.userFullName || '—'}
                      </Typography>
                      <Typography variant="caption" sx={mutedSx}>
                        #{sub.userId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{sub.userEmail}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }} title={new Date(sub.userRegisteredAt).toLocaleString()}>
                    {new Date(sub.userRegisteredAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={tierLabel(sub.tier)}
                      sx={brandChipSx('filled', {
                        bgcolor:
                          sub.tier === 'ULTIMATE'
                            ? '#7C3AED'
                            : sub.tier === 'PROFESSIONAL'
                              ? '#2563EB'
                              : sub.tier === 'TRIAL'
                                ? '#B3EC49'
                                : 'rgba(255, 255, 255, 0.08)',
                        color: sub.tier === 'TRIAL' ? 'black' : 'white',
                      })}
                    />
                    {sub.providerManaged && (
                      <Chip
                        size="small"
                        label={`Paid · ${sub.paymentProvider}`}
                        title="The user pays for this plan. Changing or cancelling it here also cancels it at the payment provider."
                        sx={brandChipSx('outlined', { ml: 1 })}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }} title={new Date(sub.startedAt).toLocaleString()}>
                    {new Date(sub.startedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {sub.creditsGranted.toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {sub.lastCreditGrantAt ? new Date(sub.lastCreditGrantAt).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Trash2 size={16} />}
                      onClick={() => handleOpenCancelModal(sub)}
                      disabled={isCancelling}
                    >
                      Cancel
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(_e: MouseEvent<HTMLButtonElement> | null, next: number) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setPageSize(Number(e.target.value))
          }
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </Paper>

      {/* Assign Subscription Modal */}
      {assignModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onKeyDown={handleKeyDown}
        >
          <div
            className="rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: greyP5 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Assign Subscription</h2>
              <button
                onClick={handleCloseAssignModal}
                className="text-gray-400 hover:text-white"
                disabled={isAssigning}
              >
                <X size={20} />
              </button>
            </div>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <Autocomplete
                options={allUsers}
                getOptionLabel={(option) => option.fullName || option.email}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  return q
                    ? options.filter(
                        (o) => o.email.toLowerCase().includes(q) || (o.fullName ?? '').toLowerCase().includes(q),
                      )
                    : options;
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{option.fullName || '—'}</Typography>
                      <Typography variant="caption" sx={mutedSx}>
                        {option.email}
                      </Typography>
                    </Box>
                  </li>
                )}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select User"
                    placeholder="Search name or email"
                    helperText={selectedUser ? selectedUser.email : undefined}
                    variant="outlined"
                  />
                )}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="assign-tier-label">Subscription Tier</InputLabel>
                <Select
                  labelId="assign-tier-label"
                  value={selectedTier}
                  label="Subscription Tier"
                  onChange={(e) => setSelectedTier(e.target.value as SubscriptionTier)}
                >
                  {tiers.map((tier) => (
                    <MenuItem key={tier} value={tier}>
                      {tierLabel(tier)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              If the user pays for a subscription through the payment provider, it is cancelled there right away and
              replaced by this plan. Unused days are not refunded; refunds are made in the payment provider&apos;s
              dashboard.
            </Alert>

            {/* Shown here: the page-level alert is hidden behind this modal. */}
            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button onClick={handleCloseAssignModal} startIcon={<X size={20} />} disabled={isAssigning}>
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                variant="contained"
                disabled={!selectedUser || isAssigning}
                startIcon={isAssigning ? <CircularProgress size={20} /> : <Check size={20} />}
                sx={{
                  bgcolor: '#B3EC49',
                  color: 'black',
                  '&:hover': {
                    bgcolor: 'rgba(179, 236, 73, 0.8)',
                  },
                  borderRadius: '24px',
                  px: 4,
                }}
              >
                Assign
              </Button>
            </Box>
          </div>
        </div>
      )}
      {/* Cancel Subscription Modal */}
      {cancelModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onKeyDown={handleKeyDown}
        >
          <div
            className="rounded-lg p-6 w-[500px] max-w-[90vw] max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: greyP5 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Cancel Subscription</h2>
              <button
                onClick={handleCloseCancelModal}
                className="text-gray-400 hover:text-white"
                disabled={isCancelling}
              >
                <X size={20} />
              </button>
            </div>

            {subscriptionToCancel && (
              <div className="mb-6">
                <p className="text-white mb-3">
                  Are you sure you want to cancel this <strong>{tierLabel(subscriptionToCancel.tier)}</strong>{' '}
                  subscription?
                </p>
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.04)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {subscriptionToCancel.userFullName || '—'}
                  </Typography>
                  <Typography variant="caption" sx={mutedSx}>
                    {subscriptionToCancel.userEmail}
                  </Typography>
                </Box>
                {subscriptionToCancel.providerManaged && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    The user pays for this plan through {subscriptionToCancel.paymentProvider}. Cancelling it here also
                    cancels it there right away: they are not charged again and lose the plan now. Unused days are not
                    refunded; refunds are made in the payment provider&apos;s dashboard.
                  </Alert>
                )}
              </div>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <div className="flex justify-end gap-2 mt-3">
              <Button onClick={handleCloseCancelModal} startIcon={<X size={20} />} disabled={isCancelling}>
                No, Keep it
              </Button>
              <Button
                onClick={handleCancel}
                variant="contained"
                color="error"
                disabled={isCancelling}
                startIcon={isCancelling ? <CircularProgress size={20} /> : <Trash2 size={20} />}
                sx={{
                  borderRadius: '24px',
                  px: 4,
                }}
              >
                Yes, Cancel Subscription
              </Button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};
